import React, {FC, useCallback, useMemo, useState} from 'react';
import FiltersPopup from '@vms/FiltersPopup';
import ActionsBar from '@vms/ActionsBar';
import FiltersButton from '@vms/FiltersButton';
import {useFiltersContext} from '@core/FiltersContext';
import {getTimeframeEnumValue, ID, LINE_TABS, LineTab, Timeframe, UnitType} from '@models';
import {iChainHistoricTrendItem, useChainsHistoricTrend} from '@api/useChainsHistoricTrend';
import {useChainHistoricTrendPptReport} from '@api/useChainHistoricTrendPptReport';
import {components} from '@api/api';
import {useUserContext} from '@core/UserContext';
import Footer from '@vms/Footer';
import {useChainPerformanceFilters} from '@core/selectors/useChainPerformanceFilters';
import useAnalyzePerformanceMarkup from "@vms/AnalyzePerformanceMarkup";
import AnalyzePerformanceChartWrapper from "@vms/AnalyzePerformanceChartWrapper"
import ChainPerformanceTimeframesTable from "@pages/ChainPerformance/TimeframesTable";
import ChainPerformanceTotalTable from "@pages/ChainPerformance/TotalTable";
import LineTabs from "@components/LineTabs";
import AnalyzePerformanceHeaderBar from "@vms/AnalyzePerformanceHeaderBar";
import AnalyzePerformanceWrapper from "@vms/AnalyzePerformanceWrapper";
import AnalyzePerformanceNavHeader from "@vms/AnalyzePerformanceNavHeader";
import AnalyzePerformanceChart from "@vms/AnalyzePerformanceChart";
import {equalInLC} from "@core/utils/string";

const ChainPerformanceContent: FC = () => {
    const {fullAccess} = useUserContext();

    const {
        options: { mainMetrics },
    } = useFiltersContext();
    const unbrandedMetric = mainMetrics.find(i => equalInLC(i.title, 'unbranded pi'))!;

    const filters = useChainPerformanceFilters();

    const [selectedUnit, setSelectedUnit] = useState<UnitType>(UnitType.Count);
    const [selectedPeriod, setSelectedPeriod] = useState<Timeframe>(Timeframe.Season);
    const [selectedMetricId, setSelectedMetricId] = useState<ID>(unbrandedMetric.id);
    const [activeTab, setActiveTab] = useState<LineTab>(LineTab.HistoricTrend);

    const payload = useMemo((): components['schemas']['ChainPerformanceFilterModel'] => {
        const indexType = selectedUnit === UnitType.Count ? null : selectedMetricId;

        return {
            ...filters,
            indexType: indexType as components['schemas']['FoodsFlavorsFilterModel']['indexType'],
            periodType: getTimeframeEnumValue(selectedPeriod),
        };
    }, [filters, selectedMetricId, selectedPeriod, selectedUnit]);

    const {data, isLoading} = useChainsHistoricTrend(
        payload,
        selectedUnit === UnitType.Count ? false : !fullAccess
    );

    const changeActiveTab = useCallback((tab: LineTab) => setActiveTab(tab), [setActiveTab])

    const markupPayload = useMemo(() => ({
        data,
        selectedPeriod,
    }), [data, selectedPeriod])
    const {
        chartItems,
        chartData,
        addChartItem,
        removeChartItem,
    } = useAnalyzePerformanceMarkup<iChainHistoricTrendItem>(markupPayload)

    const pptReport = useChainHistoricTrendPptReport(
        selectedPeriod,
        selectedUnit === UnitType.Count ? null : selectedMetricId,
        chartItems.map(i => i.id),
    );

    const noAccess = useMemo(
        () => selectedUnit === UnitType.Pct && !fullAccess,
        [selectedUnit, fullAccess]
    );

    return (
        <>
            <AnalyzePerformanceWrapper>
                <AnalyzePerformanceNavHeader
                    title={'Chain Performance'}
                    selectedUnit={selectedUnit}
                    setSelectedUnit={setSelectedUnit}
                />
                {
                    selectedUnit === UnitType.Pct && (
                        <LineTabs
                            className="ChainPerformance__line-tabs"
                            data={LINE_TABS}
                            activeTabId={activeTab}
                            onItemClick={changeActiveTab}
                            testId={"blockLineTabs"}
                        />
                    )
                }
                {
                    (selectedUnit === UnitType.Count || activeTab === LineTab.HistoricTrend) && (
                        <AnalyzePerformanceChartWrapper
                            selectedUnit={selectedUnit}
                            subTitle={'of menu launches from these chains'}
                            toolLocation={'Chain Performance'}
                        >
                            <AnalyzePerformanceHeaderBar
                                title={"Chain Activity"}
                                mainMetrics={mainMetrics}
                                selectedUnit={selectedUnit}
                                selectedPeriod={selectedPeriod}
                                selectedMetricId={selectedMetricId}
                                setSelectedMetricId={setSelectedMetricId}
                                setSelectedPeriod={setSelectedPeriod}
                            />
                            <AnalyzePerformanceChart
                                className="section"
                                isLoading={isLoading}
                                isPptLoading={pptReport.isLoading}
                                loadPptReport={pptReport.load}
                                data={data}
                                chartData={chartData}
                                removeChartItem={removeChartItem}
                                selectedUnit={selectedUnit}
                                selectedPeriod={selectedPeriod}
                                selectedMetricId={selectedMetricId}
                                title={'No chains selected'}
                            />
                        </AnalyzePerformanceChartWrapper>
                    )
                }
                {
                    !isLoading && data !== null && (selectedUnit === UnitType.Count || activeTab === LineTab.HistoricTrend) && (
                        <ChainPerformanceTimeframesTable
                            data={data}
                            isLoading={isLoading}
                            selectedPeriod={selectedPeriod}
                            selectedUnit={selectedUnit}
                            selectedMetricId={selectedMetricId}
                            chartItems={chartItems}
                            addChartItem={addChartItem}
                            removeChartItem={removeChartItem}
                        />
                    )
                }
                {
                    (selectedUnit === UnitType.Pct && activeTab === LineTab.RatingsSummary) && (
                        <ChainPerformanceTotalTable selectedMetricId={selectedMetricId}/>
                    )
                }
            </AnalyzePerformanceWrapper>
            <FiltersPopup/>
            {noAccess ? (
                <Footer/>
            ) : (
                <ActionsBar>
                    <FiltersButton id="actions-bar-filters-popup-open"/>
                    <div/>
                </ActionsBar>
            )}
        </>
    );
};

const ChainPerformance: FC = () => {
    const {isLoaded} = useFiltersContext();

    if (!isLoaded) return null;

    return <ChainPerformanceContent/>;
};

export default ChainPerformance;